import { useTranslation } from "react-i18next";
import { Icon, cn } from "@nexus/ui";

const STEP_LABELS = ["package", "terms", "questions"] as const;

interface WizardStepperProps {
  step: number;
}

export function WizardStepper({ step }: WizardStepperProps) {
  const { t } = useTranslation();

  return (
    <div
      className="flex items-center justify-center gap-3 px-6 py-3"
      role="group"
      aria-label={t("marketplace.wizard.step", { current: step, total: 3 })}
    >
      {STEP_LABELS.map((labelKey, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === step;
        const isComplete = stepNumber < step;

        return (
          <div key={labelKey} className="flex min-w-0 items-center gap-2">
            <span
              aria-current={isActive ? "step" : undefined}
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium tabular-nums transition-[background-color,color,box-shadow,transform] duration-300 ease-out motion-reduce:transition-none",
                isComplete && "bg-primary text-primary-foreground",
                isActive &&
                  "scale-110 bg-primary text-primary-foreground ring-2 ring-primary/25",
                !isComplete && !isActive && "bg-muted text-muted-foreground",
              )}
            >
              {isComplete ? (
                <Icon
                  key="check"
                  name="check"
                  size="xs"
                  color="inherit"
                  className="animate-in zoom-in-50 fade-in-0 duration-300 ease-out motion-reduce:animate-none"
                />
              ) : (
                stepNumber
              )}
            </span>
            <span
              className={cn(
                "hidden truncate text-xs transition-colors duration-300 ease-out sm:block motion-reduce:transition-none",
                isActive ? "font-medium text-foreground" : "text-muted-foreground",
              )}
            >
              {t(`marketplace.wizard.steps.${labelKey}`)}
            </span>
            {stepNumber < 3 && (
              <div
                className="relative mx-1 hidden h-px w-16 overflow-hidden bg-border sm:block"
                aria-hidden
              >
                <div
                  className={cn(
                    "absolute inset-0 origin-left bg-primary transition-transform duration-500 ease-out motion-reduce:transition-none",
                    stepNumber < step ? "scale-x-100" : "scale-x-0",
                  )}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
